import { useEffect, useRef, useState } from 'react';
import PulseShieldLoader from '../public/PulseShieldLoader';
import { isPublicShellRoutePath } from '../public/publicRoutes';
import type { WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';

const DEFAULT_TITLE = 'Loading workspace';

// Smooth progressive animation tuning.
//
// The displayed progress chases the milestone target with an ease-in-out curve:
// an ease-in ramp over the first ~320ms, then an exponential approach that slows
// as it nears the target. Progress therefore increases continuously from 0 toward
// 100 and never jumps between milestone steps, never stalls near the top, and
// never flashes a fabricated 100%. The milestone target itself comes from the
// application (calculateBootstrapProgress) — no arbitrary increments.
const APPROACH_RATE = 0.12; // per-frame exponential approach once eased in
const EASE_IN_FRAMES = 20;  // ~320ms slow-start ramp (ease-in before ease-out)
const FRAME_MS = 16;
// Displayed progress >= this counts as "reached 100%" for the completion
// sequence (the approach eases asymptotically, so it is snapped once close).
const NEAR_COMPLETE_THRESHOLD = 99;
// Hold at 100% before fading, then fade before the app is revealed.
const COMPLETE_HOLD_MS = 200;
const FADE_MS = 320;
// Minimum time the loader stays visible so an instant init never flashes 100%.
const MIN_LOADER_MS = 600;
const EXTENDED_LOADING_THRESHOLD_MS = 1200;

function clampProgress(value: number): number {
  return Math.max(0, Math.min(100, value));
}

export default function WorkspaceTransitionSurface({
  previewMode = false,
  isReady = false,
  onComplete,
  error = null,
  isTimedOut = false,
  isSlow = false,
  isAuthenticated = false,
  onRetry,
  onSignOut,
  progress = 0,
  whiteLabel = null,
  neutralBrandFallback = false,
}: {
  title?: string;
  subtitle?: string;
  previewMode?: boolean;
  showHeaderCopy?: boolean;
  isReady?: boolean;
  onComplete?: () => void;
  error?: string | null;
  isTimedOut?: boolean;
  isAuthenticated?: boolean;
  onRetry?: () => void;
  onSignOut?: () => void;
  progress?: number | null;
  isSlow?: boolean;
  whiteLabel?: WhiteLabelAppBranding | null;
  neutralBrandFallback?: boolean;
}) {
  const isPublicShellRoute = (() => {
    try {
      return typeof window !== 'undefined' && isPublicShellRoutePath(String(window.location.pathname || '/'));
    } catch {
      return false;
    }
  })();

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // ---- Smooth, milestone-driven progress ------------------------------------
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isExtendedLoading, setIsExtendedLoading] = useState(false);
  const displayedRef = useRef(0);
  const targetRef = useRef(0);
  const ageRef = useRef(0);
  const mountedAtRef = useRef(typeof window !== 'undefined' ? Date.now() : 0);
  const readyRef = useRef(isReady);
  const completionHandledRef = useRef(false);

  useEffect(() => {
    if (isPublicShellRoute || previewMode || error || isTimedOut || isReady) return;
    const timer = window.setTimeout(() => setIsExtendedLoading(true), EXTENDED_LOADING_THRESHOLD_MS);
    return () => window.clearTimeout(timer);
  }, [error, isPublicShellRoute, isReady, isTimedOut, previewMode]);

  useEffect(() => {
    readyRef.current = isReady;
  }, [isReady]);

  // The application drives the milestone target (progress). On readiness the
  // target snaps to 100 so completion always animates to a full 100%.
  const milestoneTarget = typeof progress === 'number' && Number.isFinite(progress)
    ? clampProgress(progress)
    : null;
  useEffect(() => {
    targetRef.current = isReady ? 100 : (milestoneTarget ?? displayedRef.current);
  }, [isReady, milestoneTarget]);

  useEffect(() => {
    if (isPublicShellRoute || previewMode || error || isTimedOut) return;

    let interval = 0;
    let holdTimer = 0;
    let fadeTimer = 0;

    const step = () => {
      const target = targetRef.current;
      const dx = target - displayedRef.current;
      // Ease-in over the first ~320ms, then an exponential approach that slows as
      // it nears the target — progress never jumps and never crawls near the top.
      const ramp = Math.min(1, ageRef.current / EASE_IN_FRAMES);
      ageRef.current += 1;
      displayedRef.current += dx * APPROACH_RATE * ramp;

      const settled = Math.abs(dx) < 0.5;
      if (settled) {
        displayedRef.current = target;
      }

      // Re-render only when the visible integer percent changes.
      setDisplayedProgress((prev) => (
        Math.floor(prev) === Math.floor(displayedRef.current) ? prev : displayedRef.current
      ));

      // Completion: once ready and the bar has reached ~100%, animate to exactly
      // 100%, hold briefly, then fade out and reveal the application.
      if (readyRef.current
        && onCompleteRef.current
        && displayedRef.current >= NEAR_COMPLETE_THRESHOLD
        && Date.now() - mountedAtRef.current >= MIN_LOADER_MS) {
        displayedRef.current = 100;
        setDisplayedProgress(100);
        completionHandledRef.current = true;
        window.clearInterval(interval);
        holdTimer = window.setTimeout(() => {
          setIsVisible(false); // 300ms opacity fade begins
          fadeTimer = window.setTimeout(() => {
            document.getElementById('bootstrap-shell')?.remove();
            onCompleteRef.current?.();
          }, FADE_MS);
        }, COMPLETE_HOLD_MS);
        return;
      }

      // Idle between milestones so a settled loader doesn't keep a timer running.
      if (settled && !readyRef.current) {
        window.clearInterval(interval);
      }
    };

    interval = window.setInterval(step, FRAME_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(holdTimer);
      window.clearTimeout(fadeTimer);
    };
  }, [isPublicShellRoute, previewMode, error, isTimedOut, milestoneTarget, isReady]);


  useEffect(() => {
    if (isPublicShellRoute || error || isTimedOut) {
      document.getElementById('bootstrap-shell')?.remove();
    }
  }, [error, isPublicShellRoute, isTimedOut]);

  if (isPublicShellRoute) {
    return null;
  }

  if (error || isTimedOut) {
    return (
      <div data-testid="app-workspace-opening" className="fixed inset-0 z-[9999] h-screen w-screen flex items-center justify-center bg-[#020817] text-slate-200 p-6">
        <div className="max-w-xl w-full rounded-2xl border border-slate-700/50 bg-slate-900/85 p-6 text-center shadow-2xl">
          <div className="text-lg font-semibold text-white">We couldn’t finish loading your workspace.</div>
          <div className="mt-2 text-sm text-slate-400">
            {isTimedOut && !error
              ? 'The request timed out. Please try again.'
              : 'Something went wrong while loading your account. Please try again. If the problem continues, sign out and sign back in.'}
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Retry
              </button>
            )}
            {onSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800"
              >
                {isAuthenticated ? 'Sign out' : 'Return to sign in'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const loaderSubtitle = isSlow
    ? 'Getting your workspace ready. This may take a few moments.'
    : '';

  return (
    <div data-testid="app-workspace-opening" className="fixed inset-0 z-[9999] h-screen w-screen overflow-hidden bg-[#020817] text-slate-100">
      <PulseShieldLoader
        progress={previewMode ? 0 : (progress == null ? null : displayedProgress)}
        isVisible={isVisible}
        label={DEFAULT_TITLE}
        sublabel={loaderSubtitle}
        showBrandLogo
        variant="workspace"
        whiteLabel={whiteLabel}
        neutralBrandFallback={neutralBrandFallback}
        extendedLoading={isExtendedLoading}
      />
    </div>
  );
}
